import React, { useEffect, useState } from 'react';
import { 
    Container, Typography, Button, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Paper, IconButton, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Checkbox, FormGroup, FormControlLabel, Box
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { applicationService } from '../services/application';
import { configService } from '../services/config';

const Applications = () => {
    const [apps, setApps] = useState([]);
    const [availableProviders, setAvailableProviders] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingApp, setEditingApp] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        redirectUris: '',
        enabledProviders: []
    });

    useEffect(() => {
        loadApps();
        loadAvailableProviders();
    }, []);

    const loadApps = async () => {
        try {
            const result = await applicationService.getAll();
            setApps(result.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadAvailableProviders = async () => {
        try {
            const result = await configService.getAll();
            setAvailableProviders(result.data?.map(c => c.provider) || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpen = (app = null) => {
        if (app) {
            setEditingApp(app);
            setFormData({
                name: app.name,
                redirectUris: app.redirectUris.join(', '),
                enabledProviders: app.enabledProviders || []
            });
        } else {
            setEditingApp(null);
            setFormData({
                name: '',
                redirectUris: '',
                enabledProviders: []
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async () => {
        const data = {
            ...formData,
            redirectUris: formData.redirectUris.split(',').map(s => s.trim()).filter(s => s)
        };

        try {
            if (editingApp) {
                await applicationService.update(editingApp._id, data);
            } else {
                await applicationService.create(data);
            }
            loadApps();
            handleClose();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this application?')) {
            try {
                await applicationService.delete(id);
                loadApps();
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleProviderToggle = (provider) => {
        const current = [...formData.enabledProviders];
        const index = current.indexOf(provider);
        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(provider);
        }
        setFormData({ ...formData, enabledProviders: current });
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Applications</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                    Add Application
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell>Enabled Providers</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {apps.map((app) => (
                            <TableRow key={app._id}>
                                <TableCell>{app.name}</TableCell>
                                <TableCell><code>{app.clientId}</code></TableCell>
                                <TableCell>{app.enabledProviders?.join(', ') || 'None'}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpen(app)} color="primary">
                                        <Edit />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(app._id)} color="error">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingApp ? 'Edit Application' : 'New Application'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Application Name"
                        fullWidth
                        variant="outlined"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Redirect URIs"
                        fullWidth
                        variant="outlined"
                        value={formData.redirectUris}
                        onChange={(e) => setFormData({ ...formData, redirectUris: e.target.value })}
                        sx={{ mb: 3 }}
                    />
                    
                    <Typography variant="subtitle1" gutterBottom>Enable Providers</Typography>
                    {availableProviders.length === 0 ? (
                        <Typography color="textSecondary">No providers configured. Please go to Security settings.</Typography>
                    ) : (
                        <FormGroup row>
                            {availableProviders.map((provider) => (
                                <FormControlLabel
                                    key={provider}
                                    control={
                                        <Checkbox
                                            checked={formData.enabledProviders.includes(provider)}
                                            onChange={() => handleProviderToggle(provider)}
                                        />
                                    }
                                    label={provider.charAt(0).toUpperCase() + provider.slice(1)}
                                />
                            ))}
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingApp ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Applications;
